import CmsBlock from "app/layout/cmsBlock";
import React, { useEffect, useRef } from "react";
import classes from "./lightedMirrorsPage.module.css";
import SeoHelmet from "app/layout/seoHelmet";

// The four-segment scroll indicator drawn in Figma (7049:5309) cannot follow the
// scroll position with CSS alone, so it is built and driven here rather than in
// the CMS markup. The collections slider is the row carrying the
// lighted-mirrors-category-row class, which becomes the horizontal scroll track
// on mobile. The CMS content mounts asynchronously through ReactHtmlParser, so a
// MutationObserver waits for the row before wiring anything.
const SLIDER_SEGMENTS = 4;

const wireSlider = (track) => {
  // Idempotent: never build a second indicator for the same row.
  const existing = track.nextElementSibling;
  if (existing && existing.classList.contains("lighted-mirrors-slider-indicator")) {
    return true;
  }

  const indicator = document.createElement("div");
  indicator.className = "lighted-mirrors-slider-indicator";

  // Each segment scrolls the track to its share of the range, so the four
  // buttons split the cards into four evenly spaced stops.
  const goTo = (i) => {
    const max = track.scrollWidth - track.clientWidth;
    const left = (i / (SLIDER_SEGMENTS - 1)) * max;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left, behavior: reduce ? "auto" : "smooth" });
  };

  const bars = [];
  for (let i = 0; i < SLIDER_SEGMENTS; i++) {
    const bar = document.createElement("button");
    bar.type = "button";
    bar.className = i === 0 ? "indicator-bar is-active" : "indicator-bar";
    bar.setAttribute("aria-label", `Go to slide group ${i + 1} of ${SLIDER_SEGMENTS}`);
    bar.addEventListener("click", () => goTo(i));
    indicator.appendChild(bar);
    bars.push(bar);
  }
  // A sibling after the row, not a child: a child would become a flex item of
  // the scroll track itself.
  track.parentNode.insertBefore(indicator, track.nextSibling);

  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    const progress = max > 0 ? track.scrollLeft / max : 0;
    // A proportional progress bar: map the scroll range onto the four segments.
    const active = Math.round(progress * (SLIDER_SEGMENTS - 1));
    bars.forEach((bar, i) => bar.classList.toggle("is-active", i === active));
  };

  track.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);
  update();

  return () => {
    track.removeEventListener("scroll", update);
    window.removeEventListener("resize", update);
    if (indicator.parentNode) indicator.parentNode.removeChild(indicator);
  };
};

export const LightedMirrorsPage = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const cleanups = [];

    const tryWire = () => {
      const rows = root.querySelectorAll(".lighted-mirrors-category-row");
      if (rows.length === 0) return false;
      let allWired = true;
      rows.forEach((row) => {
        const result = wireSlider(row);
        if (result === false) {
          allWired = false;
        } else if (typeof result === "function") {
          cleanups.push(result);
        }
      });
      return allWired;
    };

    if (tryWire()) {
      return () => cleanups.forEach((fn) => fn());
    }

    const observer = new MutationObserver(() => {
      if (tryWire()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className={classes.root} ref={rootRef}>
      <SeoHelmet
        title={"Lighted Mirrors"}
        url={"/lighted-mirrors"}
        description={"Lighted Mirrors"}
      />
      <CmsBlock id="lighted-mirrors" />
    </div>
  );
};
